import React, { useState, useMemo } from 'react';
import { formatCurrency } from '../../utils/formatters';

interface BrokerRevenue {
  brokerId: string;
  brokerName: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  percentOfTotal: number;
}

interface BrokerRevenueTableProps {
  brokers: BrokerRevenue[];
  concentrationThreshold?: number; // Highlight rows above this % (default 25)
  showTotals?: boolean; // Show totals row at bottom (default true)
}

type SortKey = 'brokerName' | 'upfrontCents' | 'trailCents' | 'totalCents' | 'percentOfTotal';
type SortDirection = 'asc' | 'desc';

const BrokerRevenueTable: React.FC<BrokerRevenueTableProps> = ({
  brokers,
  concentrationThreshold = 25,
  showTotals = true,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('totalCents');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Calculate totals
  const totals = useMemo(() => {
    return brokers.reduce(
      (acc, broker) => ({
        upfrontCents: acc.upfrontCents + broker.upfrontCents,
        trailCents: acc.trailCents + broker.trailCents,
        totalCents: acc.totalCents + broker.totalCents,
      }),
      { upfrontCents: 0, trailCents: 0, totalCents: 0 }
    );
  }, [brokers]);

  // Sort brokers
  const sortedBrokers = useMemo(() => {
    const sorted = [...brokers].sort((a, b) => {
      let aValue: string | number = a[sortKey];
      let bValue: string | number = b[sortKey];

      // Handle string comparison for broker names
      if (sortKey === 'brokerName') {
        return sortDirection === 'asc'
          ? (aValue as string).localeCompare(bValue as string)
          : (bValue as string).localeCompare(aValue as string);
      }

      // Numeric comparison
      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return sorted;
  }, [brokers, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) {
      return (
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (!brokers || brokers.length === 0) {
    return (
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6">
        <h3 className="font-bold text-lg mb-4 text-white">Broker Revenue Breakdown</h3>
        <div className="p-8 bg-gray-700 rounded text-center">
          <p className="text-gray-400">No broker data available for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg mt-6 overflow-hidden border border-gray-700">
      <div className="p-6 border-b border-gray-700">
        <h3 className="font-bold text-lg text-white">Broker Revenue Breakdown</h3>
        <p className="text-sm text-gray-400 mt-1">
          Individual broker contributions with concentration analysis
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-900 border-b border-gray-700">
            <tr>
              <th
                className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('brokerName')}
              >
                <div className="flex items-center gap-2">
                  Broker Name
                  <SortIcon columnKey="brokerName" />
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('upfrontCents')}
              >
                <div className="flex items-center gap-2">
                  Upfront Commission
                  <SortIcon columnKey="upfrontCents" />
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('trailCents')}
              >
                <div className="flex items-center gap-2">
                  Trail Commission
                  <SortIcon columnKey="trailCents" />
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('totalCents')}
              >
                <div className="flex items-center gap-2">
                  Total Commission
                  <SortIcon columnKey="totalCents" />
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('percentOfTotal')}
              >
                <div className="flex items-center gap-2">
                  % of Total
                  <SortIcon columnKey="percentOfTotal" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {sortedBrokers.map((broker, idx) => {
              const isHighConcentration = broker.percentOfTotal > concentrationThreshold;
              return (
                <tr
                  key={broker.brokerId || idx}
                  className={`hover:bg-gray-700 transition-colors ${
                    isHighConcentration ? 'bg-yellow-900/10' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">
                        {broker.brokerName}
                      </span>
                      {idx === 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          Top
                        </span>
                      )}
                      {isHighConcentration && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                          High Risk
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-green-400">
                      {formatCurrency(broker.upfrontCents)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-blue-400">
                      {formatCurrency(broker.trailCents)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-white font-semibold">
                      {formatCurrency(broker.totalCents)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-semibold ${
                          isHighConcentration ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                      >
                        {broker.percentOfTotal.toFixed(1)}%
                      </span>
                      {/* Visual percentage bar */}
                      <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            isHighConcentration ? 'bg-yellow-400' : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(broker.percentOfTotal, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}

            {/* Totals row */}
            {showTotals && (
              <tr className="bg-gray-900 font-semibold border-t-2 border-gray-600">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-white">Total ({brokers.length} brokers)</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-green-400">
                    {formatCurrency(totals.upfrontCents)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-blue-400">
                    {formatCurrency(totals.trailCents)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-white">
                    {formatCurrency(totals.totalCents)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-400">100.0%</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BrokerRevenueTable;
